"use client";

import React, { useCallback } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import { Switch, Text } from "@lmn/design-system";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import IconCircleInfo from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconCircleInfo.js";
import IconEco from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconEco.js";
import ResponsiveTooltip from "@/components/UI/Tooltip/ResponsiveTooltip";

const FilterWrapper = styled.div`
  width: 100%;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${({ theme }) =>
    theme.kallisto.color.background.static.backgroundPage};
  padding: 1rem;
  border-radius: 8px;

  & > svg {
    fill: ${({ theme }) => theme.kallisto.color.icon.static.iconSuccess};
  }
  & label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const SwitchLabel = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
`;

interface SustainableFilterProps {
  delayedUpdate?: boolean;
}

const SustainableFilter: React.FC<SustainableFilterProps> = ({
  delayedUpdate = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilterArray } = useFilterState();

  // Derive state directly from URL/tempFilters - no local state needed
  const currentCategories = delayedUpdate
    ? tempFilters.get("categories")?.split(",") ?? []
    : searchParams.get("categories")?.split(",") ?? [];
  const isSustainable = currentCategories.includes("101");

  const handleSustainabilityChange = useCallback(
    (checked: boolean) => {
      const categories = delayedUpdate
        ? tempFilters.get("categories")?.split(",") ?? []
        : searchParams.get("categories")?.split(",") ?? [];

      const categoriesWithoutSustainable = categories.filter(
        (id: string) => id !== "101"
      );

      const newCategories = checked
        ? [...categoriesWithoutSustainable, "101"]
        : categoriesWithoutSustainable;

      if (delayedUpdate) {
        updateTempFilterArray("categories", newCategories);
      } else {
        updateFilter(
          "categories",
          newCategories.length > 0 ? newCategories.join(",") : null
        );
      }
    },
    [
      updateFilter,
      updateTempFilterArray,
      delayedUpdate,
      searchParams,
      tempFilters,
    ]
  );

  return (
    <FilterWrapper data-testid="sustainableFilter">
      <IconEco aria-hidden="true" size={24} />
      <Switch
        checked={isSustainable}
        onChange={handleSustainabilityChange}
        // @ts-expect-error: change this in design system so that it accept React Components
        label={
          <SwitchLabel>
            <Text variant="BodySmall">Nachhaltigkeit Hotels</Text>
            <ResponsiveTooltip
              content="Auswahl an Hotels, die von unserem Partner im Bezug auf soziale, wirtschaftliche und ökologische Aspekte als besonders nachhaltig eingestuft werden."
              trigger={<IconCircleInfo size={14} />}
            />
          </SwitchLabel>
        }
        aria-describedby="sustainability-description"
        ltr={true}
      />
      <span id="sustainability-description" className="sr-only">
        Aktivieren Sie diese Option, um nur nachhaltige Reiseoptionen anzuzeigen
      </span>
    </FilterWrapper>
  );
};

export default SustainableFilter;
